import React, { ReactNode, useEffect, useState } from 'react';
import { BaseAuth } from '@habity/types/auth';
import {
  EmailAuth,
  ForgottenPassword,
  MagicLink,
  SocialAuth,
  UpdatePassword,
} from './interfaces';
import { Button, Stack, Text } from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { useRouter } from 'next/navigation';
import { VIEWS, EVENTS, ROUTES } from '@habity/constants';
import { useTranslations } from 'next-intl';
import { useTracking } from '~/hooks/useTracking';
import { TODO } from '@habity/types';
import { useAgency } from '~/hooks/useAgency';
import { SITE_URL } from '~/constants/app';
import AuthContainer from './AuthContainer';

interface AuthProps extends BaseAuth {
  children?: ReactNode;
  logo: ReactNode;
  title?: string;
  description?: ReactNode;
  agencyId: number;
  showTermsNotice?: boolean;
}

function Auth({
  supabaseClient,
  socialLayout = 'vertical',
  providers,
  view = 'sign_in',
  redirectTo,
  onlyThirdPartyProviders = false,
  linkUrl,
  magicLink = false,
  showLinks = true,
  logo,
  title,
  description,
  agencyId,
  showTermsNotice = false,
}: AuthProps): React.ReactElement | null {
  /**
   * Localization support
   */

  const [authView, setAuthView] = useState(view);
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const [defaultPassword, setDefaultPassword] = useState('');
  const [magicLinkStep, setMagicLinkStep] = useState<1 | 2>(1);
  const { push } = useRouter();
  const i18n = useTranslations('Auth');
  const { track } = useTracking();

  useEffect(() => {
    /**
     * Overrides the authview if it is changed externally
     */

    // SUPABASE: Direct call here but it's an auth hook
    const { data: authListener } = supabaseClient.auth.onAuthStateChange(
      (event: TODO) => {
        if (event === 'PASSWORD_RECOVERY') {
          setAuthView('update_password');
        } else if (event === 'USER_UPDATED') {
          setAuthView('sign_in');
        } else if (event === 'SIGNED_IN') {
          // Enhanced tracking with context about what the user was trying to do
          const trackingData: Record<string, unknown> = {
            redirectTo,
            agencyId,
            authView,
            // Include information about the action that triggered the login
            loginAction: view,
          };

          // Only access window properties in browser environment
          if (typeof window !== 'undefined') {
            // If this login was triggered by a specific home interaction
            if (window.habityLoginHomeId)
              trackingData.homeId = window.habityLoginHomeId;
            if (window.habityLoginHomeStreet)
              trackingData.homeStreet = window.habityLoginHomeStreet;
            if (window.habityLoginHomeCity)
              trackingData.homeCity = window.habityLoginHomeCity;
            // If this login was triggered by a specific action type
            if (window.habityLoginActionType)
              trackingData.actionType = window.habityLoginActionType;
          }

          track(EVENTS.SIGNED_IN, trackingData);
        }
      }
    );
    setAuthView(view);

    return () => authListener.subscription.unsubscribe();
  }, [view]);

  // /**
  //  * View handler, displays the correct Auth view
  //  * all views are wrapped in <Container/>
  //  */
  // if (authView === VIEWS.SIGN_IN || authView === VIEWS.SIGN_UP) {
  //   return (
  //     <AuthContainer
  //       logo={logo}
  //       title={title || ''}
  //       description={description}
  //       view={view}
  //     >
  //       <EmailAuth
  //         supabaseClient={supabaseClient}
  //         setAuthView={setAuthView}
  //         defaultEmail={defaultEmail}
  //         defaultPassword={defaultPassword}
  //         setDefaultEmail={setDefaultEmail}
  //         setDefaultPassword={setDefaultPassword}
  //         redirectTo={`${SITE_URL}/auth/confirm?next=${redirectTo}`}
  //         magicLink={magicLink}
  //         showLinks={showLinks}
  //         authView={authView === VIEWS.SIGN_IN ? 'sign_in' : 'sign_up'}
  //       />
  //     </AuthContainer>
  //   );
  // }

  const renderAuthContent = () => {
    if (authView === VIEWS.FORGOTTEN_PASSWORD) {
      return (
        <ForgottenPassword
          supabaseClient={supabaseClient}
          redirectTo={redirectTo}
        />
      );
    }

    if (authView === VIEWS.UPDATE_PASSWORD) {
      return <UpdatePassword supabaseClient={supabaseClient} />;
    }

    const isSignView =
      authView === VIEWS.SIGN_IN ||
      authView === VIEWS.SIGN_UP ||
      authView === VIEWS.MAGIC_LINK;

    if (isSignView) {
      return (
        <>
          {/* Only show social auth on step 1 of magic link flow */}
          {magicLinkStep === 1 && (
            <SocialAuth
              supabaseClient={supabaseClient}
              providers={providers}
              socialLayout={socialLayout}
              redirectTo={redirectTo}
              onlyThirdPartyProviders={onlyThirdPartyProviders}
              view={authView as 'sign_in' | 'sign_up'}
            />
          )}
          <MagicLink
            supabaseClient={supabaseClient}
            setAuthView={setAuthView}
            redirectTo={`${SITE_URL}/auth/continue?next=${redirectTo}`}
            showLinks={showLinks}
            agencyId={agencyId}
            onStepChange={setMagicLinkStep}
          />

          {linkUrl && magicLinkStep === 1 && (
            <Stack spacing="0.5" align="center">
              <Text fontSize="sm" color="muted">
                {i18n('shared.link_prompt')}
              </Text>
              <Button
                onClick={() => push(linkUrl)}
                variant="link"
                colorScheme="black"
                size="sm"
              >
                {i18n('shared.link_text')}
              </Button>
            </Stack>
          )}
          {showTermsNotice && (
            <Text fontSize="xs" color="subtle" textAlign="center">
              {i18n('shared.terms_text')}{' '}
              <Link href={ROUTES.CMS.TERMS} target="_blank">
                {i18n('shared.terms_link')}
              </Link>
            </Text>
          )}
        </>
      );
    }

    return null;
  };

  const content = renderAuthContent();
  if (!content) return null;

  return (
    <AuthContainer
      logo={logo}
      title={title || ''}
      description={description}
      view={view}
      hideHeader={magicLinkStep === 2}
    >
      {content}
    </AuthContainer>
  );
}

Auth.ForgottenPassword = ForgottenPassword;
Auth.UpdatePassword = UpdatePassword;
Auth.MagicLink = MagicLink;

export default Auth;
